/**
 * The Anlagenverzeichnis (§ 7 Abs 3 EStG requires one for an Einnahmen-Ausgaben-Rechner): every
 * fixed asset with what it cost, how it is written off, its AfA in the chosen year and the book
 * value left. The AfA is posted to the journal and carried into the income statement.
 */
import { Download, Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AssetDialog } from "@/components/assets/asset-dialog";
import { EmptyState } from "@/components/shared/empty-state";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useAssets } from "@/hooks/use-assets";
import { afaInput, type Asset } from "@/lib/api/assets";
import { afaSchedule, bookValueAt, depreciableCost } from "@/lib/at/afa";
import { buildCsv, downloadCsv } from "@/lib/export/tables";
import { useWorkspace } from "@/lib/session/workspace-context";
import { formatDate, formatMoney } from "@/lib/utils/format";

/** One asset in one year: what the register shows and exports. */
function yearOf(asset: Asset, year: number) {
  const input = afaInput(asset);
  const row = afaSchedule(input).find((entry) => entry.year === year);
  const started = Number(input.inServiceDate.slice(0, 4)) <= year;
  return {
    afa: row ? (row.depreciation + row.residual) / 100 : 0,
    deductible: row ? row.deductible / 100 : 0,
    bookValue: started ? bookValueAt(input, year) / 100 : depreciableCost(input),
    gone: Boolean(input.disposalDate && Number(input.disposalDate.slice(0, 4)) < year),
  };
}

export function AssetsPage() {
  const { t } = useTranslation();
  const { workspace, canWrite } = useWorkspace();
  const { data: assets, isPending } = useAssets(workspace?.id);
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [editing, setEditing] = useState<Asset | null>(null);
  const [creating, setCreating] = useState(false);

  const first = Math.min(
    thisYear,
    ...(assets ?? []).map((asset) => Number(asset.inServiceDate.slice(0, 4)) || thisYear),
  );
  const years = Array.from({ length: thisYear - first + 2 }, (_, index) => thisYear + 1 - index);
  const shown = (assets ?? [])
    .filter((asset) => Number(asset.inServiceDate.slice(0, 4)) <= year)
    .map((asset) => ({ asset, ...yearOf(asset, year) }))
    .filter((entry) => !entry.gone);
  const totals = shown.reduce(
    (sum, entry) => ({
      afa: sum.afa + entry.afa,
      deductible: sum.deductible + entry.deductible,
      bookValue: sum.bookValue + entry.bookValue,
    }),
    { afa: 0, deductible: 0, bookValue: 0 },
  );

  function exportCsv() {
    const header = [
      t("assets.name"),
      t("assets.inServiceDate"),
      t("assets.cost"),
      t("assets.usefulLife"),
      t("assets.method"),
      t("assets.disposalDate"),
      `${t("assets.afa")} ${year}`,
      `${t("assets.deductible")} ${year}`,
      `${t("assets.bookValue")} 31.12.${year}`,
    ];
    const rows = shown.map(({ asset, afa, deductible, bookValue }) => [
      asset.name,
      asset.inServiceDate.slice(0, 10),
      asset.cost,
      asset.usefulLifeYears ?? "",
      t(`assets.methods.${asset.method}`),
      (asset.disposalDate ?? "").slice(0, 10),
      afa,
      deductible,
      bookValue,
    ]);
    downloadCsv(
      `${workspace?.name ?? "export"}-anlagenverzeichnis-${year}.csv`,
      buildCsv(header, rows),
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("assets.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("assets.subtitle")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={String(year)} onValueChange={(value) => setYear(Number(value))}>
            <SelectTrigger className="w-28" aria-label={t("assets.year")} data-testid="asset-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((option) => (
                <SelectItem key={option} value={String(option)}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={exportCsv}
            disabled={shown.length === 0}
            data-testid="export-assets"
          >
            <Download /> {t("assets.export")}
          </Button>
          {canWrite && (
            <Button onClick={() => setCreating(true)} data-testid="new-asset">
              <Plus /> {t("assets.new")}
            </Button>
          )}
        </div>
      </div>

      {isPending && <Skeleton className="h-40 w-full" aria-busy="true" />}

      {assets && shown.length === 0 && (
        <EmptyState
          title={t("assets.emptyTitle", { year })}
          description={t("assets.emptyText")}
          testId="no-assets"
        />
      )}

      {shown.length > 0 && (
        <section className="space-y-3">
          <ul className="divide-y rounded-lg border bg-card" data-testid="asset-list">
            {shown.map(({ asset, afa, bookValue }) => (
              <li key={asset.id}>
                <button
                  type="button"
                  onClick={() => setEditing(asset)}
                  data-testid="asset-row"
                  className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-0.5 px-4 py-3 text-left transition-colors hover:bg-accent/40"
                >
                  <span className="truncate font-medium">{asset.name}</span>
                  <span className="text-right font-medium num" data-testid="asset-afa">
                    {formatMoney(afa)}
                  </span>
                  <span className="truncate text-xs text-muted-foreground">
                    {formatDate(asset.inServiceDate)} · {formatMoney(asset.cost)} ·{" "}
                    {asset.usefulLifeYears
                      ? t("assets.lifeShort", {
                          years: asset.usefulLifeYears,
                          method: t(`assets.methods.${asset.method}`),
                        })
                      : t("assets.gwgShort")}
                  </span>
                  <span className="text-right text-xs text-muted-foreground num">
                    {t("assets.bookValueShort", { value: formatMoney(bookValue) })}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <dl className="grid gap-1 rounded-lg border bg-card px-4 py-3 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-xs text-muted-foreground">{t("assets.totalAfa", { year })}</dt>
              <dd className="font-semibold num" data-testid="asset-total-afa">
                {formatMoney(totals.afa)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">{t("assets.totalDeductible")}</dt>
              <dd className="font-semibold num">{formatMoney(totals.deductible)}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">
                {t("assets.totalBookValue", { year })}
              </dt>
              <dd className="font-semibold num">{formatMoney(totals.bookValue)}</dd>
            </div>
          </dl>
        </section>
      )}

      <AssetDialog open={creating} onOpenChange={setCreating} />
      <AssetDialog
        open={editing !== null}
        asset={editing}
        onOpenChange={(open) => !open && setEditing(null)}
      />
    </div>
  );
}
